import type { ReactElement } from 'react';
import { Users } from 'lucide-react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { LastSeenFreshnessCell } from '@/components/shared/LastSeenFreshnessCell';
import {
  MCPNameCell,
  MCPToolsCell,
  MCPTypeBadge,
  MCPTransportBadge,
  MCPVersionCell,
  MCPStatusIndicator,
} from '@/components/shared/mcp';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function NameCell({ row }: { row: MCPServerListItem }): ReactElement {
  return (
    <MCPNameCell name={row.name} serverName={row.serverName} url={row.url} />
  );
}

export function ToolsCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPToolsCell serverName={row.name} tools={row.tools} />;
}

export function VersionCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPVersionCell row={row} />;
}

export function PublisherCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return (
    <span className="text-muted-foreground break-words whitespace-normal leading-snug">
      {row.publisher ?? '—'}
    </span>
  );
}

export function TypeCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPTypeBadge type={row.type} />;
}

export function TransportCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return <MCPTransportBadge transport={row.transport} />;
}

interface UsersCellProps {
  row: MCPServerListItem;
  onSelectServer?: (serverName: string) => void;
}

export function UsersCell({
  row,
  onSelectServer,
}: UsersCellProps): ReactElement {
  const count = (
    <span className="inline-flex items-center gap-1.5 font-medium text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      {row.userCount}
    </span>
  );

  if (!onSelectServer) {
    return count;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => onSelectServer(row.name)}
          className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
        >
          <Users className="h-3.5 w-3.5" />
          {row.userCount}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>
              {row.userCount} user{row.userCount !== 1 ? 's' : ''} with this MCP
              server
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Click to see user list
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function StatusCell({ row }: { row: MCPServerListItem }): ReactElement {
  return <MCPStatusIndicator runningCount={row.runningCount} />;
}

export function FirstSeenCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return <RelativeTimeWithTooltip date={row.firstSeenAt} />;
}

export function LastSeenCell({
  row,
}: {
  row: MCPServerListItem;
}): ReactElement {
  return <LastSeenFreshnessCell lastSeenAt={row.lastSeenAt} />;
}
